import { useEffect, useMemo, useRef, useState } from 'react';
import { preventivi } from '../../api/preventivi';
import type {
  AnteprimaCommessa,
  DettaglioPreventivo,
  QuotaSpesaInCommessa,
  RigaPreventivoLetta,
} from '../../api/preventivi';
import { formattaImporto } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';
import { discendentiArticolo } from './discendenti';

/*  COSA DI QUESTA OFFERTA SI VA A FARE.

    «Ho fatto l'impianto idraulico e l'impianto elettrico, mi confermano solo
    l'elettrico» (Andrea, riunione del 21/09/2026). Prima di questa schermata la commessa
    nasceva sempre sull'offerta intera: su quel lavoro col doppio del budget e col doppio
    del prezzo concordato, e il consuntivo avrebbe detto per mesi che il cantiere andava
    benissimo.

    DI PARTENZA E' TUTTO SPUNTATO, tranne le opzionali. È il caso di quasi ogni conferma,
    e chi non ha niente da cambiare non deve toccare niente: l'elenco e' aperto ma si
    conferma senza toccarlo.

    IL TOTALE LO CALCOLA IL SERVER. I soldi di un'offerta si contano in un posto solo, e
    una seconda formula scritta qui divergerebbe alla prima riga di dominio che cambia —
    divergendo sul budget di un cantiere. Quello che si legge scegliendo è esattamente il
    numero con cui la commessa nascerà, perché viene dalla stessa strada.
*/
export interface SceltaCommessa {
  righe: string[];
  quote: QuotaSpesaInCommessa[];
}

export function SceltaPerCommessa({
  preventivo: p,
  onCambiata,
}: {
  preventivo: DettaglioPreventivo;
  onCambiata: (scelta: SceltaCommessa | null) => void;
}) {
  const articoli = useMemo(
    () => p.righe.filter((r) => r.livello === 3 && !r.separatore),
    [p.righe],
  );

  /*  Le opzionali nascono spente perché il cliente non le ha ancora scelte: spuntarne
      una è proprio il modo di dire che l'ha presa. Tutto il resto nasce acceso.  */
  const [scelte, setScelte] = useState<Set<string>>(
    () => new Set(articoli.filter((r) => !r.opzionale).map((r) => r.id)),
  );

  /*  Le quote SCRITTE A MANO, e solo quelle: le altre le propone il server in proporzione
      a quanto del lavoro si sta prendendo, e devono poter cambiare da sole mentre si
      spunta. Tenere anche le proposte qui dentro le avrebbe congelate alla prima
      risposta.  */
  const [scritte, setScritte] = useState<Record<string, string>>({});

  const [anteprima, setAnteprima] = useState<AnteprimaCommessa | null>(null);
  /*  APERTO DI PARTENZA dal 15/10/2026. Chiuso, si confermava tutto senza accorgersi
      che si poteva scegliere — successo davanti a Galizzi. «Clicchi, ti si apre cosa vuoi
      confermare, fleghi e mandi avanti.»  */
  const [aperto, setAperto] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  const tutta = articoli.every((r) => scelte.has(r.id) === !r.opzionale);

  /*  Si chiede al server a ogni cambio, con mezzo secondo di attesa: è la stessa misura
      delle altre ricerche dell'applicativo, e senza, spuntare cinque righe di fila
      sarebbero cinque conti buttati via.  */
  const ultimaScelta = useRef('');

  useEffect(() => {
    const corpo = {
      righeInCommessa: tutta ? null : [...scelte],
      quoteSpese: Object.entries(scritte)
        .map(([spesaId, testo]) => ({ spesaId, importo: numeroDecimale(testo) ?? 0 })),
    };

    const impronta = JSON.stringify(corpo);
    if (impronta === ultimaScelta.current) return;

    let annullato = false;
    const attesa = setTimeout(() => {
      preventivi
        .anteprimaCommessa(p.id, corpo)
        .then((letta) => {
          if (annullato) return;
          ultimaScelta.current = impronta;
          setAnteprima(letta);
          setErrore(null);
        })
        .catch((e: Error) => {
          if (!annullato) setErrore(e.message);
        });
    }, 400);

    return () => {
      annullato = true;
      clearTimeout(attesa);
    };
  }, [p.id, scelte, scritte, tutta]);

  /*  Quello che il modulo di conferma manderà. Le quote viaggiano SEMPRE tutte e per
      intero — proposte comprese — perché al momento della conferma una spesa che non
      compare vale zero: è la regola che impedisce a una voce aggiunta dopo di entrare in
      commessa senza che nessuno l'abbia guardata.  */
  useEffect(() => {
    if (tutta && Object.keys(scritte).length === 0) {
      onCambiata(null);
      return;
    }

    onCambiata(
      anteprima === null ? null : { righe: [...scelte], quote: anteprima.quote },
    );
  }, [anteprima, scelte, scritte, tutta, onCambiata]);

  function spunta(riga: RigaPreventivoLetta, acceso: boolean) {
    setScelte((prima) => {
      const dopo = new Set(prima);
      if (acceso) dopo.add(riga.id);
      else dopo.delete(riga.id);
      return dopo;
    });
  }

  /*  Spuntare un capitolo spunta quello che gli sta sotto: è il gesto che serve —
      «prendo solo il capitolo uno» — e farlo riga per riga su un capitolo da venti voci
      sarebbe venti clic per una decisione sola.  */
  function spuntaCapitolo(capitolo: RigaPreventivoLetta, acceso: boolean) {
    const sotto = discendentiArticolo(capitolo, p.righe);

    setScelte((prima) => {
      const dopo = new Set(prima);
      for (const r of sotto) {
        if (acceso) dopo.add(r.id);
        else dopo.delete(r.id);
      }
      return dopo;
    });
  }

  const quotaDi = (spesaId: string) =>
    scritte[spesaId] ??
    String(anteprima?.quote.find((q) => q.spesaId === spesaId)?.importo ?? 0);

  return (
    <div className="scelta-commessa">
      <p className="riassunto">
        {tutta ? (
          <strong>Va in commessa tutta l’offerta.</strong>
        ) : (
          <strong>
            Va in commessa una parte: {scelte.size} {scelte.size === 1 ? 'voce' : 'voci'} su{' '}
            {articoli.length}.
          </strong>
        )}{' '}
        {anteprima !== null && (
          <>
            Prezzo concordato <strong>{formattaImporto(anteprima.prezzo)}</strong>, budget{' '}
            {formattaImporto(anteprima.budgetTotale)}.
          </>
        )}{' '}
        <button type="button" className="collegamento" onClick={() => setAperto(!aperto)}>
          {aperto ? 'chiudi' : 'scegli cosa'}
        </button>
      </p>

      {errore !== null && <p className="errore">{errore}</p>}

      {aperto && (
        <>
          <table className="confronto scelta-righe">
            <tbody>
              {p.righe
                .filter((r) => !r.separatore)
                .map((r) =>
                  r.livello === 3 ? (
                    <tr key={r.id} className={r.opzionale ? 'opzionale' : undefined}>
                      <td>
                        <label className="spunta">
                          <input
                            type="checkbox"
                            checked={scelte.has(r.id)}
                            onChange={(e) => spunta(r, e.target.checked)}
                          />
                          <span className={`livello-${r.livello}`}>{r.descrizione}</span>
                          {r.opzionale && <em className="motivo"> opzionale</em>}
                        </label>
                      </td>
                      <td className="destra">{formattaImporto(r.prezzo)}</td>
                    </tr>
                  ) : (
                    <tr key={r.id} className="capitolo">
                      <td>
                        <label className="spunta">
                          <input
                            type="checkbox"
                            checked={discendentiArticolo(r, p.righe).every((x) => scelte.has(x.id))}
                            onChange={(e) => spuntaCapitolo(r, e.target.checked)}
                          />
                          <span className={`livello-${r.livello}`}>{r.descrizione}</span>
                        </label>
                      </td>
                      <td className="destra">{formattaImporto(r.prezzo)}</td>
                    </tr>
                  ),
                )}
            </tbody>
          </table>

          {/*  LE SPESE HANNO UN IMPORTO E NON UNA SPUNTA, deciso da Andrea il
               28/09/2026: la progettazione l'hai fatta tutta anche se il cliente prende
               metà lavoro, gli oneri della sicurezza no. Una spunta avrebbe costretto a
               sbagliare l'una o l'altra.  */}
          {p.spese.length > 0 && (
            <table className="confronto scelta-spese">
              <tbody>
                {p.spese.map((spesa) => (
                  <tr key={spesa.id}>
                    <td>
                      <span className="nome">{spesa.nome}</span>
                      <span className="dettaglio">
                        sull’offerta intera {formattaImporto(spesa.importoCalcolato)}
                      </span>
                    </td>
                    <td className="destra">
                      <input
                        className="numero-riga"
                        inputMode="decimal"
                        aria-label={`Quanto di ${spesa.nome} va in commessa`}
                        value={quotaDi(spesa.id)}
                        onChange={(e) =>
                          setScritte((prima) => ({ ...prima, [spesa.id]: e.target.value }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="nota">
            Quello che non spunti resta sull’offerta e non entra in commessa: il
            preventivo non cambia, il cliente ha in mano quello che ha letto. Le spese si
            propongono in proporzione a quanto stai prendendo — riscrivile dove non torna.
          </p>
        </>
      )}
    </div>
  );
}
